// Fade-up-on-scroll helper. No IntersectionObserver (unreliable in some
// environments): we test the position on scroll. Respects prefers-reduced-motion.
import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const REDUCE = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useReveal() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (REDUCE) { setInView(true); return; }
    const el = ref.current;
    if (!el) return;
    let done = false;
    const check = () => {
      if (done || !ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < vh * 0.9) { done = true; setInView(true); cleanup(); }
    };
    const cleanup = () => {
      window.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
    };
    window.addEventListener('scroll', check, true);
    window.addEventListener('resize', check);
    check();
    const t1 = setTimeout(check, 60);
    const t2 = setTimeout(check, 320);
    return () => { cleanup(); clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return [ref, inView];
}

// <Reveal delay={120}>…</Reveal> — wrapper that fades up into view.
export function Reveal({ children, delay = 0, sx, ...rest }) {
  const [ref, inView] = useReveal();
  return (
    <Box
      ref={ref}
      sx={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : 'translateY(18px)',
        transition: `opacity 0.6s cubic-bezier(0.4,0,0.2,1) ${delay}ms, transform 0.6s cubic-bezier(0.4,0,0.2,1) ${delay}ms`,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}
